import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Checkbox, Col, Form, Input, InputNumber, message, Row, Space, Spin, Card, Typography, Select } from 'antd';
import { useConfig } from '../contexts/ConfigContext';
import { useRegister } from '../contexts/RegisterContext';
import { useSecureRequest } from '../hooks/useSecureRequest';
import { useCompany } from '../contexts/CompanyContext';
import { mutate } from 'swr';
import { TModuleOptions } from '../models/storageModel';
import { app } from '../config/const';
import useAuth from '../hooks/useAuth';
import { useConfigFetch } from '../hooks/useInventory';

const { Title } = Typography;
const { Option } = Select;

function toDataForm(data:any,type:string){
  let copy:any = {}
  for(const el of data){
    copy[`${type}-key-${el.id}`] = el.key
    copy[`${type}-string_value-${el.id}`] = el.string_value
    copy[`${type}-show-${el.id}`] = el.show
    copy[`${type}-numeric_value-${el.id}`] = el.numeric_value || 0
    copy[`${type}-type-${el.id}`] = el.type
    copy[`${type}-legal_code-${el.id}`] = el.legal_code
  }
  return copy
}

type GroupedData = Record<string, Array<Record<string, any>>>;
type InputObject = Record<string, any>;
/**
 * Función que agrupa las propiedades de un objeto según su prefijo y ID.
 * @param obj - Objeto de entrada con claves en el formato "grupo-propiedad-id".
 * @returns Un objeto con las claves agrupadas por prefijo y estructuradas en arrays.
 */
function groupObjectByPrefix(obj: InputObject, company_id: number, module_id: number): GroupedData {
  const grouped: Record<string, Record<string, any>[]> = {};
  const idValues: Record<string, { id: any; group: string; [key: string]: any }> = {};
  
  const typeMapping: Record<string, string> = {
    categories: 'CATEGORY_OPTION',
    displays: 'DISPLAY_OPTION',
    taxes: 'TAX',
  };

  const TRANSVERSAL_TYPES = new Set(['CATEGORY_OPTION', 'DISPLAY_OPTION', 'TAX', 'TAX_RATE']);

  // Primero, agrupamos los valores por ID
  Object.keys(obj).forEach(key => {
    const match = new RegExp(/^([a-zA-Z]+)-([a-zA-Z_]+)-(-?\w+)$/).exec(key);
    if (match) {
      const [, group, prop, id] = match;
      const targetType = typeMapping[group] || '';
      const isTransversal = TRANSVERSAL_TYPES.has(targetType);
      if (!idValues[id]) {
        idValues[id] = { 
          id: Number.parseInt(id) || id, 
          group, 
          company_id, 
          module_id: isTransversal ? null : module_id,
          type: targetType 
        };
      }
      idValues[id][prop] = obj[key];
    }
  });
  
  // Filtramos los IDs que tengan al menos un valor undefined y los excluimos
  for (const entry of Object.values(idValues)) {
    const { group, ...values } = entry;
    if (Object.values(values).includes(undefined)) {
      continue;
    }
    
    if (!grouped[group]) {
      grouped[group] = [];
    }
    grouped[group].push(values);
  }
  
  return grouped;
}

const ViewSettings: React.FC = () => {
  const {config} = useConfig()
  const {updateRegister} = useRegister()
  const {company} = useCompany()
  const [form] = Form.useForm();
  const {securePost} = useSecureRequest()
  const idCounter = useRef(-1);
  const getNextId = () => --idCounter.current;
  const moduleId = company.current?.modules.find(mod => mod.app.toUpperCase() === app.Manufacturing)?.id || 0;
  const categoryOptions = company.moduleOptions.filter(el => (el.module_id === moduleId || !el.module_id) && el.type === "CATEGORY_OPTION")
  const displayOptions = company.moduleOptions.filter(el => (el.module_id === moduleId || !el.module_id) && el.type === "DISPLAY_OPTION")
  const taxesOptions = company.moduleOptions.filter(el => (el.module_id === moduleId || !el.module_id) && el.type.includes("TAX"))

  const [categoryWarning,setCategoryWarning] = useState(false)
  const [displayWarning,setDisplayWarning] = useState(false)
  const [taxesWarning,setTaxesWarning] = useState(false)
  const [spinning,setSpinning] = useState(false)

  const [messageApi, contextHolder] = message.useMessage();

  const {tokenData} = useAuth.useToken('authCsrfToken',config.session,config.key)
  const {data} = useConfigFetch(tokenData,{
    module:moduleId,
    company:company.current?.id
  })
  if(categoryOptions.length === 0){
    categoryOptions.push({
      id: getNextId(), key: '', show: true,
      string_value: '', numeric_value: 0,
      type: 'CATEGORY_OPTION',
      company_id: company.current?.id,
      module_id: null
    })
  }
  if(displayOptions.length === 0){
    displayOptions.push({
      id: getNextId(), key: '', show: true,
      string_value: '', numeric_value: 0,
      type: 'DISPLAY_OPTION',
      company_id: company.current?.id,
      module_id: null
    })
  }
  if(taxesOptions.length === 0){
    taxesOptions.push({
      id: getNextId(), key: '', show: true,
      string_value: '', numeric_value: 0,
      type: 'TAX_RATE',
      company_id: company.current?.id,
      module_id: null
    })
  }

  const [categories, setCategories] = useState(categoryOptions);
  const [displays, setDisplays] = useState(displayOptions);
  const [taxes, setTaxes] = useState(taxesOptions);


  const [initialDataForm, setInitialDataForm] = useState({
    ...toDataForm(categoryOptions,"categories"),
    ...toDataForm(displayOptions,"displays"),
    ...toDataForm(taxesOptions,"taxes")
  })
  useEffect(()=>{
    if(data?.moduleOptions){
      console.log("Module Config Data: ",data)
      const categoryOptionsData = data.moduleOptions.filter((el:any) => el.type === "CATEGORY_OPTION")
      const displayOptionsData = data.moduleOptions.filter((el:any) => el.type === "DISPLAY_OPTION")
      const taxesOptionsData = data.moduleOptions.filter((el:any) => el.type.includes("TAX"))
      updateRegister({
        categoryOptions:categoryOptionsData,
        displayOptions:displayOptionsData,
        taxesOptions:taxesOptionsData
      })
      if(categoryOptionsData.length){
        setCategories(categoryOptionsData)
      }
      if(displayOptionsData.length){
        setDisplays(displayOptionsData)
      }
      if(taxesOptionsData.length){
        setTaxes(taxesOptionsData)
      }
      const dataForm = {
        ...toDataForm(categoryOptionsData,"categories"),
        ...toDataForm(displayOptionsData,"displays"),
        ...toDataForm(taxesOptionsData,"taxes")
      }
      console.log("Data Form: ",dataForm)
      setInitialDataForm(dataForm)
      form.setFieldsValue(dataForm)
    }
  },[data])
  
  const scanForEmptyFields = (data:Partial<TModuleOptions>[],setData:any,setShoWarning:any,key:string)=>{
    const values = form.getFieldsValue()
    let emptyValues = false
    console.log("Values: ",values)
    for(const str in values){
      emptyValues = str.includes(key) && values[str] === undefined
      if(emptyValues) {
        break
      }
    }
    let i = 0
    for(const el of data){
      const keysWithValues = Object.keys(values).filter(val => val.includes(el.id?.toString()||""))
      if(keysWithValues.length){
        data[i].key = values[keysWithValues.find(el => el.includes('key'))||""]
        data[i].string_value = values[keysWithValues.find(el => el.includes('string_value'))||""]
        data[i].show = values[keysWithValues.find(el => el.includes('show'))||""]
      }
      if(keysWithValues.length && key === "taxes"){
        data[i].numeric_value = values[keysWithValues.find(el => el.includes('numeric_value'))||""]
        data[i].type = values[keysWithValues.find(el => el.includes('type'))||""]
        data[i].legal_code = values[keysWithValues.find(el => el.includes('legal_code'))||""]
      }
      i++
    }
    if(emptyValues){
      setShoWarning(true)
    }else{
      const typeMapping: Record<string, string> = {
        categories: 'CATEGORY_OPTION',
        displays: 'DISPLAY_OPTION',
        taxes: 'TAX_RATE',
      };
      const isTransversal = ['categories', 'displays', 'taxes'].includes(key);
      const newData = [...data, { id: getNextId(), show: true, company_id: company.current?.id, module_id: isTransversal ? null : moduleId, type: typeMapping[key] || '' }]
      setData(newData);
      form.setFieldsValue(toDataForm(newData,key))
      setShoWarning(false)
    }
  }

  const onFinishForm = async (values:any)=>{
    setSpinning(true)
    const {categories:categoryOptions,
      displays:displayInventoryOptions,
      taxes:taxesOptions
    } = groupObjectByPrefix(values, company.current?.id, moduleId)
    
    const data = {
      module_options: [
        ...categoryOptions,
        ...displayInventoryOptions,
        ...taxesOptions
      ],
      company: company.current?.uid,
      path: "storage",
      module: config.module
    }
    console.log("Data: ",data)
    const moduleResponse = await securePost('/inventory/item/update-module',data)
    setSpinning(false)
    if(moduleResponse.status === 200){
      messageApi.open({
        type: "success",
        content: "Configuración actualizada"
      })
      updateRegister({categoryOptions:categoryOptions as TModuleOptions[],displayOptions:displayInventoryOptions as TModuleOptions[],taxesOptions:taxesOptions as TModuleOptions[]})
      mutate(key => Array.isArray(key) && key[0] === '/security/base/index', undefined, { revalidate: true })
    }else{
      messageApi.open({
        type: "error",
        content: "Por favor intente de nuevo"
      })
    }
  }

  return (
    <>
      {contextHolder}
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={4} style={{ margin: 0 }}>Configuración de elementos de almacén</Title>
        <Space>
          <Button htmlType='submit' form='config-form' type="primary">
            Guardar cambios
          </Button>
        </Space>
      </div>
      <Card>
        <Form 
          layout="vertical" 
          name="config-form"
          requiredMark={false}
          form={form}
          onFinish={onFinishForm}
          initialValues={initialDataForm}>
          <Spin size="large" tip="Cargando..." fullscreen spinning={spinning} />
          <h5 style={{marginLeft:"8.3333%",marginBottom:"0.5em",fontWeight:500,fontSize:"1rem"}}>Categoría de elemento</h5>
          {categories.map((valor,index) => (
            <Row key={valor.id} gutter={[10, 10]} align="middle">
              <Col span={10} sm={{span:10,offset:2}}>
                <Form.Item
                  label={index === 0 ? "Codigo categoria" : ""}
                  name={`categories-key-${valor.id}`}
                  rules={[{ required: false, message: 'Selecciona un tipo' }]}
                >
                  <Input disabled={!!valor.key} placeholder="Ingrese el valor" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label={index === 0 ? "Nombre categoria":""}
                  name={`categories-string_value-${valor.id}`}
                  rules={[{ required: false, message: 'Ingresa un valor' }]}
                >
                  <Input placeholder="Ingrese el valor" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={4} style={{marginTop: index === 0 ? "0px":"-4px"}}>
                <Form.Item
                  label={index === 0 ? "Activo":""}
                  name={`categories-show-${valor.id}`}
                  valuePropName="checked"
                  rules={[{ required: false, message: 'Ingresa un valor' }]}
                >
                  <Checkbox />
                </Form.Item>
              </Col>
            </Row>
          ))}
          <Row gutter={[10, 10]} align="middle">
            <Col span={10} sm={{span:10,offset:2}}>
              {categoryWarning && <Alert
                message="Complete los campos antes de agregar otro valor"
                banner
                closable
                onClose={()=>setCategoryWarning(false)}
              />}
              <Button type="dashed" style={{marginTop:"5px"}} onClick={() => scanForEmptyFields(categories,setCategories,setCategoryWarning,'categories')}>Agregar Categoria</Button>
            </Col>
          </Row>
          <h5 style={{marginLeft:"8.3333%",marginBottom:"0.5em",fontWeight:500,fontSize:"1rem",marginTop:"1rem"}}>Presentaciones</h5>
          {displays.map((valor,index) => (
            <Row key={valor.id} gutter={[10, 10]} align="middle">
              <Col span={10} sm={{span:10,offset:2}}>
                <Form.Item
                  label={index === 0 ? "Codigo presentación" : ""}
                  name={`displays-key-${valor.id}`}
                  rules={[{ required: false, message: 'Selecciona un tipo' }]}
                >
                  <Input disabled={!!valor.key} placeholder="Ingrese el valor" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label={index === 0 ? "Nombre presentación":""}
                  name={`displays-string_value-${valor.id}`}
                  rules={[{ required: false, message: 'Ingresa un valor' }]}
                >
                  <Input placeholder="Ingrese el valor" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={4} style={{marginTop: index === 0 ? "0px":"-4px"}}>
                <Form.Item
                  label={index === 0 ? "Activo":""}
                  name={`displays-show-${valor.id}`}
                  valuePropName="checked"
                  rules={[{ required: false, message: 'Ingresa un valor' }]}
                >
                  <Checkbox />
                </Form.Item>
              </Col>
            </Row>
          ))}
          <Row gutter={[10, 10]} align="middle">
            <Col span={10} sm={{span:10,offset:2}}>
              {displayWarning && <Alert
                message="Complete los campos antes de agregar otro valor"
                banner
                closable
                onClose={()=>setCategoryWarning(false)}
              />}
              <Button type="dashed" style={{marginTop:"5px"}} onClick={() => scanForEmptyFields(displays,setDisplays,setDisplayWarning,'displays')}>Agregar Presentación</Button>
            </Col>
          </Row>
          <h5 style={{marginLeft:"8.3333%",marginBottom:"0.5em",fontWeight:500,fontSize:"1rem",marginTop:"1rem"}}>Impuestos</h5>
          {taxes.map((valor,index) => (
            <Row key={valor.id} gutter={[10, 10]} align="middle">
              <Col xs={24} sm={{span:4,offset:2}} md={{span:4,offset:2}} lg={{span:4,offset:2}}>
                  <Form.Item
                    label={index === 0 ? "Tipo" : ""}
                    name={`taxes-type-${valor.id}`}
                    rules={[{ required: true, message: 'Selecciona un tipo' }]}
                  >
                    <Select disabled={!!valor.key && valor.id > 0}>
                      <Option value="TAX_RATE">Porcentaje</Option>
                      <Option value="TAX_FIXED">Fijo</Option>
                    </Select>
                  </Form.Item>
                </Col>
              <Col xs={12} sm={3} md={3} lg={3}>
                <Form.Item
                  label={index === 0 ? "Alias" : ""}
                  name={`taxes-key-${valor.id}`}
                  rules={[{ required: false, message: 'Ingresa un alias' }]}
                >
                  <Input disabled={!!valor.key && valor.id > 0} placeholder="Ej. IVA" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={12} sm={4} md={4} lg={4}>
                <Form.Item
                  label={index === 0 ? "Cod. Legal" : ""}
                  name={`taxes-legal_code-${valor.id}`}
                  rules={[{ required: false, message: 'Ingresa cod. dian' }]}
                >
                  <Input placeholder="Ej. 01" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={12} sm={5} md={5} lg={5}>
                <Form.Item
                  label={index === 0 ? "Nombre":""}
                  name={`taxes-string_value-${valor.id}`}
                  rules={[{ required: false, message: 'Ingresa un nombre' }]}
                >
                  <Input min={0} placeholder="Nombre del impuesto" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={12} sm={3} md={3} lg={3}>
              <Form.Item
                  shouldUpdate={(prevValues, currentValues) => prevValues[`taxes-type-${valor.id}`] !== currentValues[`taxes-type-${valor.id}`]}
                >
                  {({ getFieldValue }) => {
                    const type = getFieldValue(`taxes-type-${valor.id}`);
                    return (
                      <Form.Item
                        label={index === 0 ? "Valor" : ""}
                        name={`taxes-numeric_value-${valor.id}`}
                        rules={[{ required: false, message: 'Ingresa un valor' }]}
                      >
                         <InputNumber<number>
                            min={0 as number}
                            max={type === 'TAX_RATE' ? 100 : undefined}
                            formatter={(value) => type === 'TAX_RATE' ? `${value}%` : `${value}`}
                            parser={(value) => {
                              const val = type === 'TAX_RATE' ? value?.replace('%', '') : value;
                              return val ? Number(val) : 0;
                            }}
                            placeholder="0" 
                            style={{ width: '100%' }} 
                          />
                      </Form.Item>
                    );
                  }}
                </Form.Item>
              </Col>
              <Col xs={12} sm={2} md={3} style={{marginTop: index === 0 ? "0px":"-4px"}}>
                <Form.Item
                  label={index === 0 ? "Activo":""}
                  name={`taxes-show-${valor.id}`}
                  valuePropName="checked"
                  rules={[{ required: false, message: '' }]}
                >
                  <Checkbox />
                </Form.Item>
              </Col>
            </Row>
          ))}
          <Row gutter={[10, 10]} align="middle">
            <Col span={10} sm={{span:10,offset:2}}>
              {taxesWarning && <Alert
                message="Complete los campos antes de agregar otro valor"
                banner
                closable
                onClose={()=>setTaxesWarning(false)}
              />}
              <Button type="dashed" style={{marginTop:"5px"}} onClick={() => scanForEmptyFields(taxes,setTaxes,setTaxesWarning,'taxes')}>Agregar Impuesto</Button>
            </Col>
          </Row>
        </Form>
      </Card>
    </>
  );
};

export default ViewSettings;
